import { useRef, useState, useEffect } from 'react'
import {
    CalendarIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    EyeIcon,
    EnvelopeIcon,
    UserIcon,
    CheckCircleIcon,
    PencilIcon,
    LockClosedIcon,
    LockOpenIcon,
    UsersIcon,
} from '@heroicons/react/24/outline'
import { formatCPFOrCNPJ, formatDate, formatPhone } from '../../../Common'

const headers = [
    { label: 'Ações', icon: EyeIcon },
    { label: 'Nome', icon: UserIcon },
    { label: 'E-mail', icon: EnvelopeIcon },
    { label: 'CPF/CNPJ', icon: CheckCircleIcon },
    { label: 'Telefone', icon: CheckCircleIcon },
    { label: 'Bloqueado', icon: LockOpenIcon },
    { label: 'Criação', icon: CalendarIcon },
    { label: 'Atualização', icon: CalendarIcon },
]

const getInitials = (nome) => {
    if (!nome) return '?'
    return nome.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase()
}

const InvestidoresTable = ({ data = [], loading, viewInvestidor, editInvestidor, bloquearDesbloquearInvestidor, showInvestidor, previous, next, page, recordsPerPage }) => {
    const tableScrollRef = useRef(null)
    const tableContainerRef = useRef(null)
    const [showLeftArrow, setShowLeftArrow] = useState(false)
    const [showRightArrow, setShowRightArrow] = useState(true)
    const [arrowTop, setArrowTop] = useState('50%')

    const scrollTable = (direction) => {
        if (tableScrollRef.current) {
            tableScrollRef.current.scrollTo({
                left: tableScrollRef.current.scrollLeft + (direction === 'left' ? -300 : 300),
                behavior: 'smooth'
            })
        }
    }

    const checkScrollPosition = () => {
        if (tableScrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = tableScrollRef.current
            setShowLeftArrow(scrollLeft > 1)
            setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 1)
        }
    }

    const calculateArrowPosition = () => {
        if (tableContainerRef.current && tableScrollRef.current) {
            const cRect = tableContainerRef.current.getBoundingClientRect()
            const sRect = tableScrollRef.current.getBoundingClientRect()
            const pct = ((sRect.top - cRect.top + sRect.height / 2) / cRect.height) * 100
            setArrowTop(`${pct}%`)
        }
    }

    useEffect(() => {
        const el = tableScrollRef.current
        if (!el) return
        const timer = setTimeout(() => { checkScrollPosition(); calculateArrowPosition() }, 1)
        const onResize = () => { checkScrollPosition(); calculateArrowPosition() }
        el.addEventListener('scroll', checkScrollPosition)
        window.addEventListener('resize', onResize)
        return () => { clearTimeout(timer); el.removeEventListener('scroll', checkScrollPosition); window.removeEventListener('resize', onResize) }
    }, [data, loading])

    const ArrowBtn = ({ dir }) => (
        <button
            onClick={() => scrollTable(dir)}
            className="absolute -translate-y-1/2 z-30 p-2.5 rounded-full bg-gray-900 text-brand-yellow shadow-lg hover:opacity-90 transition-all duration-200 hidden lg:flex items-center justify-center"
            style={{ top: arrowTop, [dir === 'left' ? 'left' : 'right']: '-3.5rem' }}
        >
            {dir === 'left' ? <ChevronLeftIcon className="w-5 h-5" /> : <ChevronRightIcon className="w-5 h-5" />}
        </button>
    )

    return (
        <>
            <div className="relative" ref={tableContainerRef}>
                {showLeftArrow && <ArrowBtn dir="left" />}
                {showRightArrow && <ArrowBtn dir="right" />}

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                        <div className="p-1.5 bg-gray-900 rounded-lg">
                            <UsersIcon className="w-4 h-4 text-brand-yellow" />
                        </div>
                        <h2 className="text-sm font-semibold text-gray-900">Lista de Investidores</h2>
                    </div>

                    <div
                        ref={tableScrollRef}
                        className="overflow-x-auto"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
                        onScroll={checkScrollPosition}
                    >
                        <style>{`.inv-table-scroll::-webkit-scrollbar { display: none; }`}</style>

                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-16">
                                <span className="h-8 w-8 rounded-full border-2 border-gray-200 border-t-brand-yellow animate-spin mb-3" />
                                <p className="text-sm text-gray-400">Carregando investidores...</p>
                            </div>
                        ) : data.length > 0 ? (
                            <>
                                {/* Header da tabela — desktop */}
                                <div className="hidden lg:grid grid-cols-8 gap-2 px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                    {headers.map((h, i) => (
                                        <div key={i} className="flex items-center justify-center gap-1">
                                            <h.icon className="w-3 h-3" />{h.label}
                                        </div>
                                    ))}
                                </div>

                                <div className="divide-y divide-gray-50">
                                    {data.map((inv) => {
                                        return (
                                            <div key={inv.id} className="px-4 py-3 hover:bg-gray-50 transition-colors duration-150">
                                                {/* Linha desktop */}
                                                <div className="hidden lg:grid grid-cols-8 gap-2 items-center">
                                                    <div className="flex justify-center gap-2">
                                                        <button
                                                            onClick={() => showInvestidor?.(inv)}
                                                            className="p-1.5 bg-brand-yellow/15 text-gray-900 rounded-lg hover:bg-brand-yellow/30 transition-colors"
                                                            title="Ver detalhes"
                                                        >
                                                            <EyeIcon className="w-4 h-4" />
                                                        </button>

                                                        <button
                                                            onClick={() => editInvestidor?.(inv)}
                                                            className="p-1.5 bg-brand-yellow/15 text-gray-900 rounded-lg hover:bg-brand-yellow/30 transition-colors"
                                                            title="Editar"
                                                        >
                                                            <PencilIcon className="w-4 h-4" />
                                                        </button>

                                                        <button
                                                            onClick={() => bloquearDesbloquearInvestidor?.(inv)}
                                                            className="p-1.5 bg-brand-yellow/15 text-gray-900 rounded-lg hover:bg-brand-yellow/30 transition-colors"
                                                            title="Bloquear/Desbloquear"
                                                        >
                                                            {inv.bloqueado === 1
                                                                ? <LockClosedIcon className="w-4 h-4" />
                                                                : <LockOpenIcon className="w-4 h-4" />}
                                                        </button>
                                                    </div>

                                                    <div className="flex items-center gap-2 justify-center">
                                                        <div className="w-7 h-7 rounded-lg bg-gray-900 flex items-center justify-center text-brand-yellow text-xs font-bold flex-shrink-0">
                                                            {getInitials(inv.nome)}
                                                        </div>
                                                        <span className="text-xs font-medium text-gray-900 truncate" title={inv.nome}>
                                                            {inv.nome || '—'}
                                                        </span>
                                                    </div>

                                                    <div className="text-center">
                                                        <span className="text-xs text-gray-500 truncate" title={inv.email}>{inv.email}</span>
                                                    </div>

                                                    <div className="flex justify-center text-xs text-gray-500">
                                                        {
                                                            formatCPFOrCNPJ(inv.cpf_cnpj)
                                                        }
                                                    </div>

                                                    <div className="flex justify-center text-xs text-gray-500">
                                                        {formatPhone(inv.telefone)}
                                                    </div>

                                                    <div className="flex justify-center">
                                                        {inv.bloqueado === 1
                                                            ? <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-red-50 text-red-600"><LockClosedIcon className="w-3 h-3" />Bloqueado</span>
                                                            : <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-green-50 text-green-600"><LockOpenIcon className="w-3 h-3" />Ativo</span>}
                                                    </div>

                                                    <div className="text-center text-xs text-gray-500">
                                                        {formatDate(inv.data_criacao)}
                                                    </div>
                                                    <div className="text-center text-xs text-gray-500">
                                                        {formatDate(inv.data_atualizacao)}
                                                    </div>
                                                </div>

                                                {/* Card mobile */}
                                                <div className="lg:hidden space-y-3">
                                                    {/* Linha 1: avatar + nome + botão */}
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <div className="w-9 h-9 rounded-lg bg-gray-900 flex items-center justify-center text-brand-yellow text-xs font-bold flex-shrink-0">
                                                                {getInitials(inv.nome)}
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-semibold text-gray-900 truncate">{inv.nome || '—'}</p>
                                                                <p className="text-xs text-gray-400 truncate">{inv.email}</p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => viewInvestidor?.(inv)}
                                                            className="p-1.5 bg-brand-yellow/15 text-gray-900 rounded-lg flex-shrink-0 ml-2"
                                                        >
                                                            <EyeIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>

                                                    {/* Linha 2: CPF/CNPJ + Telefone */}
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className="bg-gray-50 rounded-lg px-3 py-2">
                                                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">CPF / CNPJ</p>
                                                            <p className="text-xs font-medium text-gray-700">{formatCPFOrCNPJ(inv.cpf_cnpj)}</p>
                                                        </div>
                                                        <div className="bg-gray-50 rounded-lg px-3 py-2">
                                                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">Telefone</p>
                                                            <p className="text-xs font-medium text-gray-700">{formatPhone(inv.telefone)}</p>
                                                        </div>
                                                    </div>

                                                    {/* Linha 3: status + e-mail verificado + datas */}
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${inv.bloqueado === 1 ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                                                            {inv.bloqueado === 1
                                                                ? <><LockClosedIcon className="w-3 h-3" />Bloqueado</>
                                                                : <><LockOpenIcon className="w-3 h-3" />Ativo</>}
                                                        </span>
                                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-gray-400 bg-gray-50">
                                                            <CalendarIcon className="w-3 h-3" />{formatDate(inv.data_criacao)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-16 text-center">
                                <div className="w-12 h-12 rounded-2xl bg-gray-900 flex items-center justify-center mb-3">
                                    <UsersIcon className="w-6 h-6 text-brand-yellow" />
                                </div>
                                <h3 className="text-sm font-semibold text-gray-900 mb-1">Nenhum investidor encontrado</h3>
                                <p className="text-xs text-gray-400">Tente ajustar os filtros de busca.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {data.length > 0 && (
                <div className="flex items-center justify-between bg-white border border-gray-100 rounded-2xl px-5 py-3 shadow-sm mt-4">
                    <span className="text-sm text-gray-500">
                        Página {(page ?? 0) + 1} · {data.length} registros
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={previous}
                            disabled={page === 0}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronLeftIcon className="w-4 h-4" /> Anterior
                        </button>
                        <button
                            onClick={next}
                            disabled={data.length < (recordsPerPage ?? 10)}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            Próximo <ChevronRightIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </>
    )
}

export default InvestidoresTable
